import * as React from 'react'

import {
  F0Box,
  F0WizardForm,
  f0FormField,
  type F0WizardFormPerSectionSubmitArg,
} from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, ExternalLink } from '@factorialco/f0-react/icons/app'
import { z } from 'zod'
import {
  AdditionalCompensationLeaveTypesDocument,
  type BulkCreateAdditionalCompensationsPageContractFragment,
  type BulkCreateAdditionalCompensationsPageEmployeeFragment,
  BulkCreateContractsAdditionalCompensationDocument,
  type BulkCreateContractsAdditionalCompensationMutationVariables,
  type ContractsPerWorkedDayDefinitionInput,
  type PayrollConceptSelectFieldFragment,
  ReviewEmployeesModalDocument,
  type ReviewEmployeesModalEmployeeFragment,
} from 'generated/resources/graphql'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { CurrencyContextProvider } from 'modules/compensations/lib/CurrencyContext'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import {
  type AdditionalCompensationWizardFormSchema,
  useAdditionalCompensationWizardForm,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/useAdditionalCompensationWizardForm'
import {
  type AdditionalCompensationWizardAmountStrategyType,
  type BasicStepValues,
  FIXED_AMOUNT_STRATEGY,
  isBasicStepValues,
  isRecurrenceValue,
  isRawDefinitionStepValues,
  type RawDefinitionStepValues,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/lib/additionalCompensationWizard.shared'
import {
  buildMappedAdditionalCompensation,
  createBasicContextFromValues,
  normalizeDefinitionValues,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/lib/additionalCompensationWizard.utils'
import { manageUpperLimit } from 'modules/contracts/lib/amountStrategyUtils'

interface Props {
  companyId: string
  contracts: BulkCreateAdditionalCompensationsPageContractFragment[]
  selectedEmployees: BulkCreateAdditionalCompensationsPageEmployeeFragment[]
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  onRemoveEmployee?: (employeeId: string) => void
  onSuccess: () => void
  onClose?: () => void
}

const BULK_CREATE_FORM_NAME = 'bulk-create-additional-compensations'

type ReviewSectionSchema = {
  review: ReturnType<typeof buildReviewSchema>
}

type BulkCreateAdditionalCompensationsWizardFormSchema =
  AdditionalCompensationWizardFormSchema<ReviewSectionSchema>

function ReviewEmployeesStepContent({
  employeeIds,
  onRemoveEmployee,
}: {
  employeeIds: string[]
  onRemoveEmployee: (employeeId: string) => void
}) {
  const { navigation } = useNavigation()
  const reviewCollectionId = React.useId()

  React.useEffect(() => {
    const reviewCollectionElement = document.getElementById(reviewCollectionId)
    const formElement = reviewCollectionElement?.closest('form')

    if (!reviewCollectionElement || !formElement) {
      return undefined
    }

    const handleSubmit = (event: Event) => {
      if (!(event instanceof SubmitEvent)) {
        return
      }

      if (!(event.submitter instanceof HTMLElement)) {
        return
      }

      if (!reviewCollectionElement.contains(event.submitter)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    }

    formElement.addEventListener('submit', handleSubmit, true)

    return () => {
      formElement.removeEventListener('submit', handleSubmit, true)
    }
  }, [reviewCollectionId])

  const source = useGraphqlDataCollectionSource(
    {
      document: ReviewEmployeesModalDocument,
      fetchPolicy: 'network-only',
      mapVariables: () => ({
        employeeIds,
        sort: {
          field: 'firstName',
          order: 'asc',
        },
      }),
      mapPagination: (data) => {
        const connection = data?.employees?.employeesConnection

        return {
          total: connection?.totalCount ?? 0,
          cursor: connection?.pageInfo?.endCursor ?? undefined,
          hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        }
      },
      mapData: (data) => data?.employees?.employeesConnection?.nodes ?? [],
      paginationType: 'infinite-scroll',
      itemActions: (employee: ReviewEmployeesModalEmployeeFragment) => {
        const agreementAction = {
          label: i18n.t(
            'contract_versions.additional_compensation.bulk_create.modal.open_contract'
          ),
          icon: ExternalLink,
          type: 'primary' as const,
          onClick: () => {
            window.open(
              navigation.employees.id.contractVersions.fullPath({
                id: employee.id.toString(),
              }),
              '_blank',
              'noopener,noreferrer'
            )
          },
        }

        if (employeeIds.length === 1) {
          return [agreementAction]
        }

        return [
          agreementAction,
          {
            label: i18n.t('contract_versions.additional_compensation.bulk_create.modal.remove'),
            icon: Delete,
            type: 'primary' as const,
            onClick: () => {
              onRemoveEmployee(employee.id)
            },
          },
        ]
      },
    },
    [employeeIds]
  )

  return (
    <F0Box id={reviewCollectionId} paddingX="xl">
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition: (item: ReviewEmployeesModalEmployeeFragment) => ({
                title: item.fullName,
                avatar: {
                  type: 'person',
                  src: buildUrl(item.avatar?.url) ?? '',
                  firstName: item.firstName,
                  lastName: item.lastName,
                },
              }),
              fields: [
                {
                  label: i18n.t('common.team'),
                  render: (item: ReviewEmployeesModalEmployeeFragment) => ({
                    type: 'tag',
                    value: {
                      label: getEmployeeTeamsLabel(item),
                    },
                  }),
                },
              ],
            },
          },
        ]}
      />
    </F0Box>
  )
}

function buildReviewSchema({
  employeeIds,
  onRemoveEmployee,
}: {
  employeeIds: string[]
  onRemoveEmployee: (employeeId: string) => void
}) {
  return z.object({
    reviewContent: f0FormField(z.string().optional(), {
      label: '',
      fieldType: 'custom',
      render: () => (
        <ReviewEmployeesStepContent employeeIds={employeeIds} onRemoveEmployee={onRemoveEmployee} />
      ),
    }),
  })
}

const getEmployeeTeamsLabel = (employee: ReviewEmployeesModalEmployeeFragment) => {
  const teams = employee.memberships.map((membership) => membership.team?.name).filter(Boolean)

  return (
    teams.join(', ') ||
    i18n.t('contract_versions.additional_compensation.bulk_create.modal.without_team')
  )
}

export const buildBulkCreateMutationVariables = ({
  companyId,
  selectedEmployees,
  selectedContracts,
  payrollConcepts,
  basicValues,
  definitionValues,
}: {
  companyId: string
  selectedEmployees: BulkCreateAdditionalCompensationsPageEmployeeFragment[]
  selectedContracts: BulkCreateAdditionalCompensationsPageContractFragment[]
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  basicValues: BasicStepValues
  definitionValues: RawDefinitionStepValues
}): BulkCreateContractsAdditionalCompensationMutationVariables & {
  perWorkedDayDefinition?: ContractsPerWorkedDayDefinitionInput | null
} => {
  const selectedContract = selectedContracts[0]

  if (selectedEmployees.length === 0 || !selectedContract?.id) {
    throw new Error(
      i18n.t('contract_versions.additional_compensation.bulk_create.errors.empty_selection')
    )
  }

  const additionalCompensation = buildMappedAdditionalCompensation({
    basicValues,
    definitionValues: normalizeDefinitionValues(definitionValues),
    companyId,
    contractVersionId: selectedContract.id,
    payrollConcepts,
    createTemporaryId: () => BULK_CREATE_FORM_NAME,
  })

  const recurrence = isRecurrenceValue(additionalCompensation.recurrence)
    ? additionalCompensation.recurrence
    : isRecurrenceValue(basicValues.recurrence)
      ? basicValues.recurrence
      : 'monthly'

  if (!additionalCompensation.payrollConceptId) {
    throw new Error(
      i18n.t('contract_versions.additional_compensation.bulk_create.errors.missing_payroll_concept')
    )
  }

  return {
    companyId,
    employeeIds: selectedEmployees.map((employee) => employee.id),
    payrollConceptId: additionalCompensation.payrollConceptId,
    description: additionalCompensation.description,
    firstPaymentOn:
      additionalCompensation.firstPaymentOn ??
      i18nDateToString(basicValues.firstPaymentOn, 'backend'),
    recurrence,
    amountStrategyType: additionalCompensation.amountStrategyType,
    amount: additionalCompensation.amountStrategy?.amount,
    upperLimit: manageUpperLimit(additionalCompensation.amountStrategy),
    perWorkedDayDefinition: additionalCompensation.perWorkedDayDefinition,
  }
}

export const getLatestEffectiveOn = (
  contracts: BulkCreateAdditionalCompensationsPageContractFragment[]
) => {
  const effectiveDates = contracts
    .map((contract) => contract.effectiveOn)
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => left.localeCompare(right))

  return effectiveDates.at(-1)
}

export const getSelectedContracts = (
  contracts: BulkCreateAdditionalCompensationsPageContractFragment[],
  selectedEmployees: BulkCreateAdditionalCompensationsPageEmployeeFragment[]
) => {
  const selectedEmployeeIds = new Set(selectedEmployees.map((employee) => employee.id))

  return contracts.filter((contract) => selectedEmployeeIds.has(contract.employee.id))
}

export const getDefaultFirstPaymentDate = () => {
  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth(), 1)
}

const BulkCreateAdditionalCompensationsRevamp = ({
  companyId,
  contracts,
  selectedEmployees,
  payrollConcepts,
  onRemoveEmployee,
  onSuccess,
  onClose,
}: Props) => {
  const [currentSelectedEmployees, setCurrentSelectedEmployees] = React.useState(selectedEmployees)

  React.useEffect(() => {
    setCurrentSelectedEmployees(selectedEmployees)
  }, [selectedEmployees])

  const [basicContext, setBasicContext] = React.useState<{
    amountStrategyType?: AdditionalCompensationWizardAmountStrategyType
    payrollConceptId?: string
  }>({
    amountStrategyType: FIXED_AMOUNT_STRATEGY,
    payrollConceptId: undefined,
  })

  const { data: leaveTypesData } = useQuery(AdditionalCompensationLeaveTypesDocument, {
    variables: { companyId },
  })

  const [bulkCreateContractsAdditionalCompensation] = useMutation(
    BulkCreateContractsAdditionalCompensationDocument,
    {
      extractorFn: (result) => result.contractsMutations.bulkCreateContractsAdditionalCompensation,
    }
  )

  const payrollConceptOptions = useCategorizedPayrollConcepts(payrollConcepts)
  const selectedContracts = React.useMemo(
    () => getSelectedContracts(contracts, currentSelectedEmployees),
    [contracts, currentSelectedEmployees]
  )
  const effectiveOn = React.useMemo(
    () => getLatestEffectiveOn(selectedContracts),
    [selectedContracts]
  )
  const defaultFirstPaymentDate = React.useMemo(() => getDefaultFirstPaymentDate(), [])
  const handleRemoveEmployee = React.useCallback(
    (employeeId: string) => {
      setCurrentSelectedEmployees((currentEmployees) => {
        if (currentEmployees.length === 1) {
          return currentEmployees
        }

        return currentEmployees.filter((employee) => employee.id !== employeeId)
      })
      onRemoveEmployee?.(employeeId)
    },
    [onRemoveEmployee]
  )
  const reviewSchema = React.useMemo(
    () =>
      buildReviewSchema({
        employeeIds: currentSelectedEmployees.map((employee) => employee.id),
        onRemoveEmployee: handleRemoveEmployee,
      }),
    [currentSelectedEmployees, handleRemoveEmployee]
  )

  const leaveTypeOptions = React.useMemo(
    () =>
      leaveTypesData?.timeoff?.leaveTypesConnection?.nodes?.map((leaveType) => ({
        id: leaveType.id,
        label: leaveType.translatedName || leaveType.name,
      })) ?? [],
    [leaveTypesData]
  )

  const handleSubmit = React.useCallback(
    async ({
      sectionId,
      data,
      fullData,
    }: F0WizardFormPerSectionSubmitArg<BulkCreateAdditionalCompensationsWizardFormSchema>) => {
      if (sectionId === 'basic') {
        if (!isBasicStepValues(data)) {
          return { success: false }
        }

        setBasicContext(createBasicContextFromValues(data))

        return { success: true }
      }

      if (sectionId === 'definition') {
        return { success: true }
      }

      const basicValues = fullData.basic
      const definitionValues = fullData.definition

      if (!basicValues || !definitionValues) {
        return { success: false }
      }

      if (!isBasicStepValues(basicValues) || !isRawDefinitionStepValues(definitionValues)) {
        return { success: false }
      }

      const result = await bulkCreateContractsAdditionalCompensation({
        variables: buildBulkCreateMutationVariables({
          companyId,
          selectedEmployees: currentSelectedEmployees,
          selectedContracts,
          payrollConcepts,
          basicValues,
          definitionValues,
        }),
      })

      const handled = handleResult(result, () => undefined)

      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('contract_versions.additional_compensation.bulk_create.errors.generic'),
          time: 2000,
        })

        return { success: false }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('contract_versions.additional_compensation.bulk_create.success'),
        time: 2000,
      })

      onSuccess()

      return { success: true }
    },
    [
      bulkCreateContractsAdditionalCompensation,
      companyId,
      onSuccess,
      currentSelectedEmployees,
      payrollConcepts,
      selectedContracts,
    ]
  )

  const { formDefinition, steps } = useAdditionalCompensationWizardForm<ReviewSectionSchema>({
    formName: BULK_CREATE_FORM_NAME,
    submitLabel: i18n.t('contract_versions.additional_compensation.bulk_create.create'),
    payrollConcepts,
    payrollConceptOptions,
    effectiveOn,
    defaultFirstPaymentDate,
    getFirstPaymentOnValidationError: ({
      firstPaymentOn,
      defaultFirstPaymentDate: minimumFirstPaymentDate,
    }) => {
      if (!minimumFirstPaymentDate) {
        return undefined
      }

      const selectedMonth = new Date(firstPaymentOn.getFullYear(), firstPaymentOn.getMonth(), 1)
      const minimumMonth = new Date(
        minimumFirstPaymentDate.getFullYear(),
        minimumFirstPaymentDate.getMonth(),
        1
      )

      if (selectedMonth >= minimumMonth) {
        return undefined
      }

      return i18n.t(
        'contract_versions.additional_compensation.errors.first_payment_on_before_current_month'
      )
    },
    basicContext,
    leaveTypeOptions,
    extraSections: {
      review: reviewSchema,
    },
    extraSectionConfig: {
      review: {
        title: i18n.t(
          'contract_versions.additional_compensation.bulk_create.modal.employees_counter',
          {
            totalItems: currentSelectedEmployees.length,
          }
        ),
        withInset: false,
      },
    },
    extraSteps: [
      {
        title: i18n.t('common.review'),
        sectionIds: ['review'],
        nextLabel: i18n.t('contract_versions.additional_compensation.bulk_create.create'),
      },
    ],
    onSubmit: handleSubmit,
  })

  return (
    <CurrencyContextProvider>
      <F0WizardForm
        isOpen
        title={i18n.t('employees.bulk_edit.additional_compensations')}
        width="xl"
        formDefinition={formDefinition}
        steps={steps}
        nextLabel={i18n.t('common.continue')}
        previousLabel={i18n.t('common.back')}
        onClose={onClose}
      />
    </CurrencyContextProvider>
  )
}

export default BulkCreateAdditionalCompensationsRevamp
